import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./AdminApplications.css";
import SearchIcon from "@mui/icons-material/Search";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import CloseIcon from "@mui/icons-material/Close";
import useAdminHttp from "../../../hooks/use-admin-http";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8000/api/v1";

const PROFILE_TABS = [
  { key: "all", label: "All" },
  { key: "employed", label: "Employed" },
  { key: "student", label: "Student" },
  { key: "self-employed", label: "Self-employed" },
];

const PROFILE_CLASSES = {
  employed: "wla-status--shortlisted",
  student: "wla-status--review",
  "self-employed": "wla-status--new",
};

// Status codes as used by the backend; slug drives the CSS modifier class.
const STATUS_OPTIONS = [
  { code: 0, slug: "not_started", label: "Not started" },
  { code: 1, slug: "in_progress", label: "In progress" },
  { code: 2, slug: "reviewed", label: "Reviewed" },
  { code: 3, slug: "potential", label: "Potential" },
  { code: 4, slug: "proposed", label: "Proposed" },
  { code: 5, slug: "rejected", label: "Rejected" },
  { code: 6, slug: "accepted", label: "Accepted" },
  { code: 7, slug: "cancelled", label: "Cancelled" },
];

const STATUS_BY_CODE = Object.fromEntries(
  STATUS_OPTIONS.map((option) => [option.code, option])
);

const PRIORITY_VALUES = ["1", "2", "3", "4", "5"];

// Team member ids as used by the backend's assigned_to field.
const TEAM_MEMBERS = [
  { id: 68, name: "Abiel Bramaditya" },
  { id: 71, name: "Anja Gulden" },
  { id: 69, name: "Ayca Gulsen" },
  { id: 54, name: "Bart Ravelli" },
  { id: 67, name: "Gonzalo Benitez" },
  { id: 80, name: "Joy van Geesink" },
  { id: 77, name: "Justin Lucaci" },
  { id: 66, name: "Nazgol Khatibi" },
  { id: 78, name: "Paul Minde" },
  { id: 44, name: "Sam England" },
  { id: 10, name: "Tony England" },
];

const TEAM_MEMBERS_BY_ID = Object.fromEntries(
  TEAM_MEMBERS.map(({ id, name }) => [id, name])
);

const AVATAR_COLORS = [
  "#eef3fb",
  "#fdeee0",
  "#efeefb",
  "#e6f2ec",
  "#f7f1e4",
];

const rentingKey = (application) =>
  application.renting_type ? application.renting_type.toLowerCase() : null;

const initialsOf = (application) =>
  (application.client_name || "")
    .split("/")
    .map((part) => part.trim()[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase() || "?";

const shortId = (id) => (id ? `${id.slice(0, 8)}…` : "—");

const formatMoney = (value) =>
  value == null
    ? null
    : new Intl.NumberFormat("nl-NL", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
      }).format(value);

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (isNaN(date)) return null;
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const applicationAddress = (application) => {
  const street = [
    application.street,
    application.house_number,
    application.floor,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    [street, application.city].filter(Boolean).join(", ") ||
    application.property_name ||
    null
  );
};

const NoteModal = ({ application, onClose }) => {
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  if (!application) return null;

  return (
    <div className="wla-modal-overlay" onClick={onClose}>
      <div
        className="wla-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wla-note-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="wla-modal-header">
          <div>
            <h2 id="wla-note-title" className="wla-modal-title">
              Notes
            </h2>
            <p className="wla-modal-sub">{application.client_name}</p>
          </div>
          <button
            type="button"
            className="wla-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </div>
        <div className="wla-modal-body">{application.notes}</div>
      </div>
    </div>
  );
};

const AdminApplications = () => {
  const [applications, setApplications] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [listingFilter, setListingFilter] = useState("all");
  // Triage filters, applied by the backend as query params. "all" means the
  // param is omitted.
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  const [activeFilter, setActiveFilter] = useState("1");
  const navigate = useNavigate();
  const [noteApplication, setNoteApplication] = useState(null);
  const { isLoading, error, sendRequest } = useAdminHttp();

  const resetFilters = () => {
    setSearch("");
    setListingFilter("all");
    setStatusFilter("all");
    setPriorityFilter("all");
    setAssigneeFilter("all");
    setActiveFilter("1");
  };

  useEffect(() => {
    const params = new URLSearchParams();
    if (statusFilter !== "all") params.set("status", statusFilter);
    if (priorityFilter !== "all") params.set("priority", priorityFilter);
    if (assigneeFilter !== "all") params.set("assigned_to", assigneeFilter);
    if (activeFilter !== "all") params.set("is_active", activeFilter);
    const query = params.toString();
    sendRequest(
      {
        url: `${BACKEND_URL}/welocate/applications${query ? `?${query}` : ""}`,
      },
      (data) => setApplications(data || [])
    );
  }, [sendRequest, statusFilter, priorityFilter, assigneeFilter, activeFilter]);

  const listingOptions = useMemo(() => {
    const map = new Map();
    applications.forEach((a) => {
      if (a.listing_id && !map.has(a.listing_id)) {
        map.set(a.listing_id, applicationAddress(a) || shortId(a.listing_id));
      }
    });
    return [...map.entries()].map(([id, label]) => ({ id, label }));
  }, [applications]);

  const filtered = useMemo(() => {
    return applications
      .filter((a) => {
        if (activeTab !== "all" && rentingKey(a) !== activeTab) return false;
        if (listingFilter !== "all" && a.listing_id !== listingFilter)
          return false;
        if (search) {
          const q = search.toLowerCase();
          const haystack = `${a.client_name || ""} ${a.phone || ""} ${
            a.mobile || ""
          } ${a.property_name || ""} ${a.city || ""} ${
            a.notes || ""
          }`.toLowerCase();
          if (!haystack.includes(q)) return false;
        }
        return true;
      });
  }, [applications, activeTab, search, listingFilter]);

  const tabCount = (key) =>
    key === "all"
      ? applications.length
      : applications.filter((a) => rentingKey(a) === key).length;

  return (
    <div className="wla-applications">
      <h1 className="wla-page-title">Applications</h1>
      <p className="wla-page-subtitle">
        {applications.length} applications across {listingOptions.length}{" "}
        listed properties
      </p>

      <div className="wla-panel">
        <div className="wla-tabs">
          {PROFILE_TABS.map((tab) => (
            <button
              key={tab.key}
              className={`wla-tab${
                activeTab === tab.key ? " wla-tab--active" : ""
              }`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}{" "}
              <span className="wla-tab-count">{tabCount(tab.key)}</span>
            </button>
          ))}
          <button className="wla-reset-btn" onClick={resetFilters}>
            Reset filters
          </button>
        </div>

        <div className="wla-toolbar">
          <div className="wla-search">
            <SearchIcon className="wla-search-icon" />
            <input
              type="text"
              placeholder="Search name, phone, property..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="wla-property-select">
            <select
              value={listingFilter}
              onChange={(e) => setListingFilter(e.target.value)}
            >
              <option value="all">All properties</option>
              {listingOptions.map(({ id, label }) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className="wla-property-select">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All statuses</option>
              {STATUS_OPTIONS.map(({ code, label }) => (
                <option key={code} value={code}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className="wla-property-select">
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
            >
              <option value="all">All priorities</option>
              {PRIORITY_VALUES.map((value) => (
                <option key={value} value={value}>
                  Priority {value}
                </option>
              ))}
            </select>
          </div>
          <div className="wla-property-select">
            <select
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
            >
              <option value="all">Active & inactive</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
          </div>
          <div className="wla-property-select">
            <select
              value={assigneeFilter}
              onChange={(e) => setAssigneeFilter(e.target.value)}
            >
              <option value="all">All assignees</option>
              {TEAM_MEMBERS.map(({ id, name }) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="wla-table-wrapper">
          <table className="wla-table">
            <thead>
              <tr>
                <th>Applicant</th>
                <th>Listing</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Assigned to</th>
                <th>Renting type</th>
                <th>Offer</th>
                <th>Received</th>
                <th className="wla-cell-docs">Documents</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={10} className="wla-empty">
                    Loading applications...
                  </td>
                </tr>
              )}
              {!isLoading && error && (
                <tr>
                  <td colSpan={10} className="wla-empty">
                    {error}
                  </td>
                </tr>
              )}
              {!isLoading &&
                !error &&
                filtered.map((a, i) => (
                  <tr
                    // One client can have several screenings, so the client id
                    // is not unique per row; the screening id is.
                    key={a.screening_id ?? a.id}
                    className="wla-row"
                    onClick={() => navigate(`/admin/applications/${a.id}`)}
                  >
                    <td className="wla-cell-applicant">
                      <div className="wla-applicant">
                        <span
                          className="wla-applicant-avatar"
                          style={{
                            background: AVATAR_COLORS[i % AVATAR_COLORS.length],
                          }}
                        >
                          {initialsOf(a)}
                        </span>
                        <div className="wla-applicant-text">
                          <div
                            className="wla-applicant-name wla-cell-truncate"
                            title={a.client_name || undefined}
                          >
                            {a.client_name}
                          </div>
                          {(a.phone || a.mobile) && (
                            <div className="wla-cell-secondary wla-cell-truncate">
                              {[...new Set([a.phone, a.mobile])]
                                .filter(Boolean)
                                .join(" · ")}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="wla-cell-listing">
                      <span
                        className="wla-cell-primary wla-cell-truncate"
                        title={applicationAddress(a) || undefined}
                      >
                        {applicationAddress(a) || shortId(a.listing_id)}
                      </span>
                      {a.property_price != null && (
                        <div className="wla-cell-secondary">
                          {formatMoney(a.property_price)}/mo asking
                        </div>
                      )}
                    </td>
                    <td>
                      <span
                        className={`wla-status wla-tstatus--${
                          STATUS_BY_CODE[a.status]?.slug || "not_started"
                        }`}
                      >
                        <span className="wla-status-dot" />
                        {STATUS_BY_CODE[a.status]?.label || "Not started"}
                      </span>
                    </td>
                    <td>
                      {a.priority != null ? (
                        <span className="wla-priority">{a.priority}</span>
                      ) : (
                        <span className="wla-cell-secondary">—</span>
                      )}
                    </td>
                    <td>
                      {a.assigned_to != null ? (
                        <span className="wla-cell-primary">
                          {a.assigned_to_name ||
                            TEAM_MEMBERS_BY_ID[a.assigned_to] ||
                            a.assigned_to}
                        </span>
                      ) : (
                        <span className="wla-cell-secondary">Unassigned</span>
                      )}
                    </td>
                    <td>
                      {a.renting_type ? (
                        <span
                          className={`wla-status ${
                            PROFILE_CLASSES[rentingKey(a)] || ""
                          }`}
                        >
                          <span className="wla-status-dot" />
                          {a.renting_type}
                        </span>
                      ) : (
                        <span className="wla-cell-secondary">Unknown</span>
                      )}
                      {a.contract_length && (
                        <div className="wla-cell-secondary">
                          {a.contract_length}
                        </div>
                      )}
                    </td>
                    <td>
                      {a.rental_offer != null ? (
                        <span className="wla-cell-primary">
                          {formatMoney(a.rental_offer)}/mo
                        </span>
                      ) : (
                        <span className="wla-cell-secondary">—</span>
                      )}
                      {a.total_salary > 0 && (
                        <div className="wla-cell-secondary">
                          {formatMoney(a.total_salary)} income
                        </div>
                      )}
                    </td>
                    <td>
                      {formatDate(a.received_on) ? (
                        <span className="wla-cell-secondary">
                          {formatDate(a.received_on)}
                        </span>
                      ) : (
                        <span className="wla-cell-secondary">—</span>
                      )}
                    </td>
                    <td className="wla-cell-docs">
                      {a.document_upload_count > 0 ? (
                        <span className="wla-priority">
                          {a.document_upload_count}
                        </span>
                      ) : (
                        <span className="wla-cell-secondary">0</span>
                      )}
                    </td>
                    <td className="wla-cell-notes">
                      {a.notes ? (
                        <button
                          type="button"
                          className="wla-note-btn"
                          title="View notes"
                          aria-label={`View notes for ${a.client_name}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setNoteApplication(a);
                          }}
                        >
                          <StickyNote2OutlinedIcon />
                        </button>
                      ) : (
                        <span className="wla-cell-secondary">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              {!isLoading && !error && filtered.length === 0 && (
                <tr>
                  <td colSpan={10} className="wla-empty">
                    {applications.length === 0
                      ? "No applications yet."
                      : "No applications match your search."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <NoteModal
        application={noteApplication}
        onClose={() => setNoteApplication(null)}
      />
    </div>
  );
};

export default AdminApplications;
